import { useCallback, useMemo, useRef, useState } from 'react';
import { useParams, NavLink as RouterLink } from 'react-router';

import {
  CheckCircleRounded,
  Error,
  Warning,
  InfoOutlined,
  WarningAmber
} from '@mui/icons-material';
import {
  Typography,
  Chip,
  Stack,
  Box,
  Link,
  Tooltip,
  useTheme,
  Avatar,
  Badge
} from '@mui/material';
import {
  getGridSingleSelectOperators,
  getGridStringOperators
} from '@mui/x-data-grid-pro';

import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { makeStyles } from 'tss-react/mui';

import UpdatesDialog from '@/components/Updates';
import { evaluationsConfig } from '@/components/Updates/config/config';
import DataTable from '@/components/dataTable';
import ChipsColumn from '@/components/dataTable/customChipsColumn';
import FilterCreators from '@/components/dataTable/customFilters/FilterCreators';
import creatorAndGroupsOperator from '@/components/dataTable/customOperators/creatorAndGroupsOperator';
import dateRangeOperator from '@/components/dataTable/customOperators/dateRangeOperator';
import UpdatesIcon from '@/components/icons/UpdatesIcon';
import PLATFORM_MAPPER from '@/constants/platformMapper';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@/constants/table';
import { useFetchPosts } from '@/hooks/queries/posts';
import useTableParams from '@/hooks/useTableParams';
import useWorkspaceNavigate from '@/hooks/useWorkspaceNavigate';
import { getPrettyDate, formatDate } from '@/utils/datesAndTime';
import getRoute from '@/utils/getRoute/index';
import Toolbar from '@/views/evaluations/detail/body/_partials/Toolbar';

import BooleanColumn from './BooleanColumn';

const getAdRatingLabel = (value) => {
  const score = Math.max(1, Math.round(value * 10));
  return `Score - ${score}`;
};

const defaultParams = {
  page: DEFAULT_PAGE,
  size: DEFAULT_PAGE_SIZE,
  sort: undefined,
  order: undefined,
  compliance: undefined,
  platformDisclosure: undefined,
  tags: undefined,
  hashtags: undefined,
  platform: undefined,
  createTime: undefined,
  keywords: undefined,
  keywordGroups: undefined,
  visualRecognition: undefined,
  adType: undefined,
  isRelevant: undefined,
  isRated: undefined,
  creatorIds: undefined,
  creatorGroupIds: undefined
};

const defaultColumnWidths = {
  adType: 130,
  compliance: 140,
  keywords: 165,
  vrDetected: 165,
  tags: 165,
  hashtags: 165,
  labels: 165,
  platform: 115,
  createTime: 140,
  creator: 155,
  platformDisclosure: 85,
  isRelevant: 80,
  isRated: 80,
  adRating: 130
};

const useStyles = makeStyles()((theme) => ({
  table: {
    '& .MuiDataGrid-pinnedColumnHeaders, & .MuiDataGrid-pinnedColumns': {
      backgroundColor: theme.palette.common.white
    },
    cursor: 'pointer'
  }
}));

const arrayToString = (array) => array.map((item) => item.keyword).join(',');

const getKeywordsFrom = (keywordMatches) => {
  if (!Array.isArray(keywordMatches)) return [];

  return keywordMatches
    .filter((item) => item?.value)
    .map((item) => ({
      keyword: item.value,
      tooltip: Array.isArray(item.groups) ? item.groups.join(', ') : ''
    }));
};

const getLabelsFrom = (labels) => {
  if (!Array.isArray(labels)) return [];

  return labels
    .filter((label) => label?.name)
    .map((label) => ({
      keyword: label.name,
      tooltip: label.category?.name || ''
    }));
};

const getInitialFilterModelItems = (params) => {
  const {
    createTime,
    hashtags,
    platform,
    compliance,
    adType,
    isRelevant,
    isRated,
    platformDisclosure,
    tags,
    visualRecognition,
    creatorIds,
    creatorGroupIds
  } = params;

  const items = [];
  const creatorsArray = creatorIds ? creatorIds.split(',') : [];
  const creatorGroupsArray = creatorGroupIds ? creatorGroupIds.split(',') : [];
  const creators = [...creatorsArray, ...creatorGroupsArray];

  if (creators.length > 0) {
    items.push({
      field: 'creator',
      id: items.length,
      operator: 'isAnyOf',
      value: creators.join(',')
    });
  }
  if (compliance !== undefined)
    items.push({
      field: 'compliance',
      id: items.length,
      operator: 'isAnyOf',
      value: compliance.split(',')
    });
  if (adType !== undefined) {
    items.push({
      field: 'adType',
      id: items.length,
      operator: 'isAnyOf',
      value: adType.split(',')
    });
  }
  if (isRelevant !== undefined) {
    items.push({
      field: 'isRelevant',
      id: items.length,
      operator: 'is',
      value: isRelevant
    });
  }
  if (isRated !== undefined) {
    items.push({
      field: 'isRated',
      id: items.length,
      operator: 'is',
      value: isRated
    });
  }
  if (platformDisclosure !== undefined)
    items.push({
      field: 'platformDisclosure',
      id: items.length,
      operator: 'is',
      value: platformDisclosure
    });
  if (tags !== undefined)
    tags.split(',').forEach((item) =>
      items.push({
        field: 'tags',
        id: items.length,
        operator: 'contains',
        value: item
      })
    );
  if (hashtags !== undefined)
    hashtags.split(',').forEach((item) =>
      items.push({
        field: 'hashtags',
        id: items.length,
        operator: 'contains',
        value: item
      })
    );
  if (platform !== undefined)
    items.push({
      field: 'platform',
      id: items.length,
      operator: 'isAnyOf',
      value: platform.split(',')
    });
  if (visualRecognition !== undefined) {
    items.push({
      field: 'visualRecognition',
      id: items.length,
      operator: 'is',
      value: visualRecognition.split(',')
    });
  }
  if (createTime !== undefined)
    items.push({
      field: 'createTime',
      id: items.length,
      operator: 'between',
      value: createTime.split(',').map((item) => new Date(item))
    });

  return items;
};

const Results = ({ setSelectionModel, selectionModel }) => {
  const theme = useTheme();
  const { classes } = useStyles();
  const queryClient = useQueryClient();
  const navigate = useWorkspaceNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvaluation, setSelectedEvaluation] = useState();
  const evaluationId = Number(useParams().evaluationId);

  const { params, handlers, paramsInTheUrl, updateParams } = useTableParams(
    getRoute.evaluations.DETAIL(evaluationId),
    defaultParams
  );

  const columnWidths = useRef(defaultColumnWidths);

  const { requestedServices, parameters } = queryClient.getQueryData([
    'evaluation',
    evaluationId
  ]);

  const hasVRForMinorsSelected = requestedServices?.some(
    (service) => service.type === 'minors'
  );

  const initialFilterModel = useRef({
    items: getInitialFilterModelItems(params)
  });

  const {
    data: posts,
    isFetching,
    isLoading
  } = useFetchPosts({
    params: {
      evaluationId,
      ...params
    },
    enabled: !!evaluationId
  });
  const evaluation = posts?.results.find(
    (v) => v.id === selectedEvaluation?.postId
  );

  const complianceMapper = useMemo(
    () => ({
      compliant: {
        icon: <CheckCircleRounded color="success" />,
        text: 'Compliant'
      },
      questionable: {
        icon: <Warning color="warning" />,
        text: 'Questionable'
      },
      violation: {
        icon: <Error color="error" />,
        text: 'Violation'
      },
      unknown: {
        icon: <></>,
        text: ''
      }
    }),
    []
  );

  const columns = useMemo(
    () => [
      {
        field: 'adRating',
        headerName: 'Ad Score',
        type: 'string',
        width: columnWidths.current.adRating,
        filterable: false,
        visible: process.env.REACT_APP_ENV !== 'production',
        sortable: true,
        renderHeader: (params) => (
          <Tooltip title="Estimate of how likely it is that this post contains advertising.">
            <Typography fontWeight="500">{params.colDef.headerName}</Typography>
          </Tooltip>
        ),
        renderCell: (params) => {
          if (!params.row.adRating) {
            return <Box width="100%">—</Box>;
          }

          return (
            <Chip
              label={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="subtitle2" fontWeight={500}>
                    {getAdRatingLabel(params.row.adRating)}
                  </Typography>
                  <Tooltip
                    arrow
                    placement="top"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          maxWidth: 500,
                          width: 'max-content'
                        }
                      }
                    }}
                    title={
                      <Box>
                        <Typography
                          fontWeight="bold"
                          mb={1}
                          variant="subtitle2"
                        >
                          Ad Score
                        </Typography>
                        <Typography variant="body2" mb={2}>
                          Influencer Monitor makes an estimation of how likely
                          each post is to contain advertising. 10 means a high
                          chance of commercial content, whereas 1 means that the
                          post is predicted to be organic.
                        </Typography>
                        <Typography fontWeight="bold" variant="body2">
                          1 - Organic . 5 - Questionable . 1O - Advertisement
                        </Typography>
                      </Box>
                    }
                  >
                    <InfoOutlined />
                  </Tooltip>
                </Stack>
              }
              sx={{
                color: theme.palette.common.white,
                backgroundColor: theme.palette.action.active
              }}
            />
          );
        }
      },
      {
        field: 'isRated',
        headerName: 'Rated',
        type: 'boolean',
        width: columnWidths.current.isRated,
        sortable: false,
        renderCell: (data) => <BooleanColumn value={data.row.isRated} />
      },
      {
        field: 'adType',
        headerName: 'Type',
        type: 'singleSelect',
        valueOptions: [
          { value: 'advertisement', label: 'Advertisement' },
          { value: 'questionable', label: 'Questionable' },
          { value: 'organic', label: 'Organic' },
          { value: 'unknown', label: 'Empty' }
        ],
        valueFormatter: (params) => params.value || '—',
        filterOperators: getGridSingleSelectOperators().filter(
          (operator) => operator.value === 'isAnyOf'
        ),
        width: columnWidths.current.adType,
        sortable: false,
        renderCell: (data) => {
          if (!data.row.adType) {
            return <>—</>;
          }
          if (data.row.adType === 'questionable') {
            return <Typography>Possible Ad</Typography>;
          }
          return (
            data.row.adType && (
              <Typography>
                {data.row.adType.charAt(0).toUpperCase() +
                  data.row.adType.slice(1)}
              </Typography>
            )
          );
        }
      },
      {
        field: 'compliance',
        headerName: 'Compliance',
        type: 'singleSelect',
        valueOptions: [
          { value: 'compliant', label: 'Compliant' },
          { value: 'questionable', label: 'Questionable' },
          { value: 'violation', label: 'Violation' },
          { value: 'unknown', label: 'Empty' }
        ],
        filterOperators: getGridSingleSelectOperators().filter(
          (operator) => operator.value === 'isAnyOf'
        ),
        valueFormatter: (params) => params.value || '—',
        width: columnWidths.current.compliance,
        sortable: false,
        renderCell: (data) => {
          if (
            data.row.adType === 'organic' ||
            data.row.isRelevant === false ||
            data.row.compliance === 'unknown'
          ) {
            return <>—</>;
          }

          return (
            <Stack direction="row" alignItems="center" gap="4px">
              {complianceMapper[data.row.compliance]?.icon}
              <Typography>
                {complianceMapper[data.row.compliance]?.text}
              </Typography>
            </Stack>
          );
        }
      },
      {
        field: 'isRelevant',
        headerName: 'Relevant',
        type: 'boolean',
        width: columnWidths.current.isRelevant,
        sortable: false,
        valueFormatter: (params) => params.value || '—',
        renderCell: (data) => {
          if (data.row.isRelevant === null) {
            return <>—</>;
          }
          return <BooleanColumn value={data.row.isRelevant} />;
        }
      },
      {
        field: 'platform',
        headerName: 'Platform',
        headerClassName: 'evaluation-detail-platform',
        type: 'singleSelect',
        valueOptions: Object.keys(PLATFORM_MAPPER).map((key) => ({
          value: key,
          label: PLATFORM_MAPPER[key]
        })),
        filterOperators: getGridSingleSelectOperators().filter(
          (operator) => operator.value === 'isAnyOf'
        ),
        width: columnWidths.current.platform,
        sortable: false,
        renderCell: (data) =>
          data.row.platform &&
          (data.row.url ? (
            <Chip
              onClick={() =>
                data.row.url && window.open(data.row.url, '_blank')
              }
              label={PLATFORM_MAPPER[data.row.platform]}
              sx={{
                cursor: 'pointer',
                backgroundColor: theme.palette.action.selected
              }}
            />
          ) : (
            <Tooltip title="Link unavailable" arrow>
              <Chip
                label={PLATFORM_MAPPER[data.row.platform]}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: theme.palette.action.selected
                }}
              />
            </Tooltip>
          ))
      },
      {
        field: 'creator',
        headerName: 'Creator',
        headerClassName: 'evaluation-detail-creator',
        width: columnWidths.current.creator,
        filterOperators: creatorAndGroupsOperator.map((operator) => ({
          ...operator,
          InputComponent: (props) => (
            <FilterCreators {...props} evaluationId={evaluationId} />
          )
        })),
        renderCell: (data) => {
          const { creator, collaborators } = data.row;

          const displayName = creator.name.startsWith('@')
            ? creator.name.slice(1)
            : creator.name;

          const existingCollaborators = collaborators
            .filter((collab) => collab.id !== null)
            .map((collab) => collab.name);

          if (creator.id) {
            const groups = parameters?.creatorGroups?.map((g) => g.name) || [];

            const tooltipTitle = creator.groups.map((grp, i) => (
              <span
                key={grp}
                style={{ fontWeight: groups.includes(grp) ? 'bold' : 'normal' }}
              >
                {grp}
                {i < creator.groups.length - 1 && ', '}
              </span>
            ));

            return (
              <Tooltip
                title={tooltipTitle}
                disableHoverListener={!creator.groups.length}
              >
                <Link
                  component={RouterLink}
                  onClick={(event) => {
                    event.preventDefault(); // Prevent default link behavior
                    navigate(getRoute.creators.DETAIL(data.row.creator.id));
                  }}
                  color="primary"
                  style={{ width: '100%' }} // Ensure link container uses full width
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    overflow="hidden"
                    gap={1}
                  >
                    <Avatar
                      src={creator.avatarUrl || ''}
                      alt={creator.name}
                      sx={{ width: 30, height: 30, fontSize: 16 }}
                    />
                    <Typography
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap', // Ensure single line with ellipsis
                        flexShrink: 1, // Allow shrinking of the text to fit the space
                        minWidth: 0 // Prevent the element from exceeding container width
                      }}
                      id="creatorName"
                    >
                      {displayName}
                    </Typography>
                  </Stack>
                </Link>
              </Tooltip>
            );
          }

          if (existingCollaborators.length > 0)
            return (
              <Tooltip
                arrow
                title={
                  <>
                    This creator is not in your list.{' '}
                    {existingCollaborators.map((name, index) => (
                      <span key={index} style={{ fontWeight: 'bold' }}>
                        {name}
                        {index < existingCollaborators.length - 1 ? ', ' : ''}
                      </span>
                    ))}{' '}
                    {existingCollaborators.length > 1 ? 'are' : 'is'}{' '}
                    {existingCollaborators.length > 1
                      ? 'collaborators'
                      : 'a collaborator'}{' '}
                    on this post.
                  </>
                }
              >
                <Stack
                  direction="row"
                  gap={1}
                  alignItems="center"
                  sx={{ width: '100%' }}
                >
                  <InfoOutlined color="secondary" fontSize="small" />
                  <Typography
                    sx={{
                      color: theme.palette.secondary.main,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap', // Ensure single line with ellipsis
                      flexShrink: 1, // Allow shrinking of the text to fit the space
                      minWidth: 0 // Prevent the element from exceeding container width
                    }}
                    id="creatorName"
                    color="error"
                  >
                    {displayName}
                  </Typography>
                </Stack>
              </Tooltip>
            );

          return (
            <Tooltip arrow title="This creator has been deleted">
              <Stack
                direction="row"
                gap={1}
                alignItems="center"
                sx={{ width: '100%' }}
              >
                <WarningAmber color="error" fontSize="small" />
                <Typography
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap', // Ensure single line with ellipsis
                    flexShrink: 1, // Allow shrinking of the text to fit the space
                    minWidth: 0 // Prevent the element from exceeding container width
                  }}
                  id="creatorName"
                  color="error"
                >
                  {displayName}
                </Typography>
              </Stack>
            </Tooltip>
          );
        }
      },
      {
        field: 'platformDisclosure',
        headerName: 'Sponsorship',
        type: 'boolean',
        width: columnWidths.current.platformDisclosure,
        sortable: false,
        renderCell: (data) => (
          <BooleanColumn value={data.row.platformDisclosure} />
        )
      },
      {
        field: 'keywords',
        headerName: 'Keywords',
        filterable: false,
        width: columnWidths.current.keywords,
        sortable: false,
        valueFormatter: (params) => arrayToString(params.value),
        renderCell: (data) => {
          if (data.row.isExpired) return <>—</>;

          return (
            <ChipsColumn
              data={data.row.keywords}
              chipColor="opacity75"
              cellWidth={data.colDef.computedWidth}
            />
          );
        }
      },
      {
        field: 'visualRecognition',
        headerName: 'VR detected',
        type: 'singleSelect',
        valueOptions: [
          { value: '0-17', label: 'Age 0-17' },
          { value: '0-6', label: 'Age 0-6' },
          { value: '7-14', label: 'Age 7-14' },
          { value: '15-17', label: 'Age 15-17' }
        ],
        filterOperators: getGridSingleSelectOperators().filter(
          (operator) => operator.value === 'is'
        ),
        filterable: hasVRForMinorsSelected,
        width: columnWidths.current.vrDetected,
        sortable: false,
        valueFormatter: (params) => arrayToString(params.value),
        renderCell: (data) => {
          if (data.row.isExpired) return <>—</>;

          return (
            <ChipsColumn
              data={data.row.visualRecognition}
              chipColor="opacity75"
              cellWidth={data.colDef.computedWidth}
            />
          );
        }
      },
      {
        field: 'tags',
        headerName: 'Tags',
        filterOperators: getGridStringOperators().filter(
          (operator) => operator.value === 'contains'
        ),
        width: columnWidths.current.tags,
        sortable: false,
        valueFormatter: (params) => arrayToString(params.value),
        renderCell: (data) => {
          if (data.row.isExpired) return <>—</>;

          return (
            <ChipsColumn
              data={data.row.tags}
              chipColor="secondary"
              cellWidth={data.colDef.computedWidth}
            />
          );
        }
      },
      {
        field: 'hashtags',
        headerName: 'Hashtags',
        filterOperators: getGridStringOperators().filter(
          (operator) => operator.value === 'contains'
        ),
        width: columnWidths.current.hashtags,
        sortable: false,
        valueFormatter: (params) => arrayToString(params.value),
        renderCell: (data) => {
          if (data.row.isExpired) return <>—</>;

          return (
            <ChipsColumn
              data={data.row.hashtags}
              chipColor="secondary"
              cellWidth={data.colDef.computedWidth}
            />
          );
        }
      },
      {
        field: 'labels',
        headerName: 'Labels',
        filterable: false,
        width: columnWidths.current.labels,
        sortable: false,
        valueFormatter: (params) => arrayToString(params.value),
        renderCell: (data) => {
          if (data.row.isExpired) return <>—</>;

          return (
            <ChipsColumn
              data={data.row.labels}
              chipColor="default"
              cellWidth={data.colDef.computedWidth}
            />
          );
        }
      },
      {
        field: 'createTime',
        headerName: 'Publication Date',
        type: 'date',
        filterOperators: dateRangeOperator,
        width: columnWidths.current.createTime,
        valueFormatter: (params) => String(params.value),
        renderCell: (data) => (
          <Typography variant="body2">
            {getPrettyDate(data.row.createTime)}
          </Typography>
        )
      },
      {
        field: 'changes',
        headerName: 'Changes',
        minWidth: 80,
        type: 'boolean',
        filterable: false,
        sortable: false,
        flex: 1,
        renderCell: (params) => {
          const { updates, compliance } = params.row;
          const rowIndex = params.api.getRowIndexRelativeToVisibleRows(
            params.id
          );
          const updatesCount = updates?.length || 0;
          const currentRow = posts?.results[rowIndex || 0];
          const creatorName = currentRow?.creator?.name || '';
          const violation = compliance === 'violation';

          return (
            <Box
              onClick={(e) => {
                if (violation) {
                  setIsModalOpen(true);
                  setSelectedEvaluation({
                    postId: params.row.id,
                    evaluationId: params.row.evaluationId,
                    creatorName,
                    updates
                  });
                }
                e.stopPropagation();
              }}
              position="relative"
              display="inline-block"
              sx={{
                backgroundColor: violation
                  ? theme.palette.common.black
                  : theme.palette.grey[300],
                ...(violation && {
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.1)'
                  }
                })
              }}
            >
              <Badge
                badgeContent={updatesCount}
                invisible={!violation}
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: 'secondary.main',
                    color: 'common.white'
                  }
                }}
              >
                <Stack
                  width={30}
                  height={30}
                  alignItems="center"
                  justifyContent="center"
                  direction="column"
                >
                  <UpdatesIcon fill={theme.palette.common.white} />
                </Stack>
              </Badge>
            </Box>
          );
        }
      }
    ],
    [posts, columnWidths.current]
  );

  const onColumnResize = useCallback(({ colDef }) => {
    const { field, width } = colDef;
    columnWidths.current = { ...columnWidths.current, [field]: width };
  }, []);

  const onFilterModelChange = useCallback(
    (params) => {
      const newParams = {};

      // adds active filters to the newParams object
      params.items.forEach((item) => {
        const { field, value } = item;

        if (field === 'creator' && Array.isArray(value)) {
          if (value.length === 0) {
            newParams.creatorIds = undefined;
            newParams.creatorGroupIds = undefined;
            return; // Continue to the next filter item
          }

          // This existing logic is still correct for when there are selections
          const creators = [];
          const creatorGroups = [];

          value.forEach((option) => {
            if (option.type === 'Creators') {
              creators.push(String(option.id));
            } else if (option.type === 'Groups') {
              creatorGroups.push(String(option.id));
            }
          });

          newParams.creatorIds =
            creators.length > 0 ? creators.join(',') : undefined;
          newParams.creatorGroupIds =
            creatorGroups.length > 0 ? creatorGroups.join(',') : undefined;
        } else if (field === 'createTime' && value !== undefined) {
          const formattedDates = value
            .map((date) => formatDate(date, 'yyyy-MM-dd'))
            .filter(Boolean);

          newParams.createTime = formattedDates.length
            ? formattedDates.join(',')
            : undefined;
        } else if (typeof value === 'object') {
          newParams[field] = value.join(',');
        } else if (field === 'hashtags' || field === 'tags') {
          newParams[field] = newParams[field]
            ? `${newParams[field]},${value}`
            : value;
        } else {
          newParams[field] = value;
        }
      });

      // sets removed filters as undefined so that they get deleted from the url
      columns
        .filter((column) => column.filterable !== false)
        .forEach((column) => {
          const filterExistsInTheUrl = Object.keys(paramsInTheUrl).includes(
            column.field
          );
          const isFilterActive = params.items.some(
            (item) => item.field === column.field
          );
          if (column.field === 'creator' && !isFilterActive) {
            newParams.creatorIds = undefined;
            newParams.creatorGroupIds = undefined;
          } else if (filterExistsInTheUrl && !isFilterActive)
            newParams[column.field] = undefined;
        });

      updateParams({
        ...paramsInTheUrl,
        ...newParams,
        page: DEFAULT_PAGE
      });
    },
    [params]
  );

  const getHashtagValue = (item) => {
    if (typeof item === 'object' && item?.hashtagName) return item.hashtagName;
    if (typeof item === 'string') return item;
    return null;
  };

  const formattedData = useMemo(
    () =>
      posts?.results.map((post) => ({
        creator: post?.creator,
        createTime: post.createTime,
        hashtags: post.hashtags
          .map((hashtag) => {
            const value = getHashtagValue(hashtag);
            if (!value) return null;

            return {
              keyword: value.startsWith('#') ? value : `#${value}`,
              tooltip: ''
            };
          })
          .filter(Boolean),
        id: post.id,
        url: post.url,
        keywords: getKeywordsFrom(post.evaluation.keywordMatches),
        labels: getLabelsFrom(post.labels),
        platform: post.platform,
        compliance: post.evaluation.compliance,
        adType: post.evaluation.adType,
        isRelevant: post.evaluation.isRelevant,
        isRated: post.evaluation.isRated,
        platformDisclosure: post.platformDisclosure,
        tags: post.tags.map((tags) => ({
          keyword: `@${tags.username}`,
          tooltip: ''
        })),
        adRating: post.adRating,
        visualRecognition: post.visualRecognition?.map((service) => {
          const intervals = [
            { min: 0, max: 6 },
            { min: 7, max: 14 },
            { min: 15, max: 17 }
          ];

          const intervalsToShow = intervals
            .filter((interval) =>
              service.predictedAges.some(
                (average) => interval.min <= average && average <= interval.max
              )
            )
            .map((interval) => ({
              keyword: `Age ${interval.min}-${interval.max}`,
              tooltip: 'Children'
            }));

          return intervalsToShow[0] || [];
        }),
        collaborators: post.collaborators,
        updates: post.evaluation.updates,
        isExpired: post.isExpired,
        evaluationId
      })),
    [posts]
  );

  const dataCache = useRef({});

  formattedData?.forEach((item) => {
    dataCache.current[item.id] = item;
  });

  const expiredStatus = (() => {
    const expiredCount = selectionModel.filter(
      (id) => dataCache.current[id]?.isExpired
    ).length;
    if (expiredCount === 0) return 'none';
    if (expiredCount === selectionModel.length) return 'all';
    return 'some';
  })();

  return (
    <Box>
      <DataTable
        checkboxSelection
        className={classes.table}
        columns={columns}
        filterMode="server"
        paginationMode="server"
        sortingMode="server"
        loading={isLoading || isFetching}
        onChangePage={handlers.onChangePage}
        onFilterModelChange={onFilterModelChange}
        onChangePageSize={handlers.onChangePageSize}
        onSortModelChange={handlers.onSortModelChange}
        onColumnResize={onColumnResize}
        onRowSelectionModelChange={setSelectionModel}
        keepNonExistentRowsSelected
        rowCount={posts?.totalItems}
        rowHeight={56}
        rows={formattedData || []}
        rowSelectionModel={selectionModel}
        page={params.page}
        size={params.size}
        sort={params.sort}
        order={params.order}
        multipleFiltersColumns={['tags', 'hashtags']}
        initialState={{
          pinnedColumns: {
            right: ['changes']
          },
          filter: {
            filterModel: initialFilterModel.current
          }
        }}
        onRowClick={(data, event) => {
          if (
            event.target.id !== 'creatorName' &&
            !!data.row.platform &&
            !event.target.className.includes('MuiChip')
          ) {
            const newParams = { ...params };
            delete newParams.page;
            delete newParams.size;

            navigate(
              getRoute.evaluations.POST(evaluationId, data.id, newParams)
            );
          }
        }}
        toolbar={() => (
          <Toolbar
            selectionModel={selectionModel}
            setSelectionModel={setSelectionModel}
            expiredStatus={expiredStatus}
          />
        )}
        columnVisibilityModel={{
          adRating: process.env.REACT_APP_ENV !== 'production',
          visualRecognition: hasVRForMinorsSelected
        }}
      />
      <UpdatesDialog
        isOpen={isModalOpen}
        context={selectedEvaluation}
        updates={evaluation}
        onClose={() => setIsModalOpen(false)}
        config={evaluationsConfig}
      />
    </Box>
  );
};

Results.propTypes = {
  selectionModel: PropTypes.array,
  setSelectionModel: PropTypes.func
};

export default Results;
